import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { authRoutes, standalonePublicRoutes, appRoutes, standaloneAppRoutes } from './routeConfig';
import ProtectedRoute from './ProtectedRoute';
import CopilotProvider from '../components/copilot/CopilotProvider';
import PermissionRoute from './PermissionRoute';
import MainLayout from '@components/layouts/MainLayout';
import AuthLayout from '@components/layouts/AuthLayout';
import { Loader2 } from 'lucide-react';

const NotFound = lazy(() => import('@components/placeholders/NotFound'));
const SALogin = lazy(() => import('@components/superAdmin/SALogin'));
const SAPortal = lazy(() => import('@components/superAdmin/SAPortal'));
const ClientLayout = lazy(() => import('@components/clientPortal/ClientLayout'));
const ClientLogin = lazy(() => import('@components/clientPortal/ClientLogin'));
const ClientPortalPage = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientDashboard })));
const ClientRoadmap = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientRoadmap })));
const ClientRequests = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientRequests })));
const ClientApprovals = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientApprovals })));
const ClientBudget = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientBudget })));
const ClientMessages = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientMessages })));
const ClientTasks = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientTasks })));
const ClientDocuments = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientDocuments })));
const ClientReportPage = lazy(() => import('@components/clientPortal/ClientPortalPage').then(m => ({ default: m.ClientReport })));
const ClientCalendarPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientCalendar })));
const ClientSchedulingPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientScheduling })));
const ClientKBPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientKnowledgeBase })));
const ClientTimeLogsPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientTimeLogs })));
const ClientMilestonesPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientMilestones })));
const ClientActivityPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientActivity })));
const ClientAIChatPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientAIChat })));
const ClientDelaysPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientDelays })));
const ClientDigestPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientDigest })));
const ClientTrendsPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientTrends })));
const ClientSprintsPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientSprints })));
const ClientGoalsPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientGoals })));
const ClientNotificationsPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientNotifications })));
const ClientForumPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientForum })));
const ClientSurveysPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientSurveys })));
const ClientUploadsPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientUploads })));
const ClientChangelogPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientChangelog })));
const ClientPreferencesPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientPreferences })));
const ClientFinancePage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientFinancePage })));
const ClientMultiDashboardPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientMultiDashboard })));
const ClientTeamContactsPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientTeamContacts })));
const ClientMeetingHistoryPage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientMeetingHistory })));
const ClientProfilePage = lazy(() => import('@components/clientPortal/ClientPortalExtras').then(m => ({ default: m.ClientProfile })));

/**
 * Full-page loading spinner (shown while lazy components load)
 */
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-surface-50">
    <div className="flex flex-col items-center gap-3 animate-fade-in">
      <div className="w-10 h-10 rounded-2xl bg-primary-50 flex items-center justify-center">
        <Loader2 size={20} className="text-primary-600 animate-spin" />
      </div>
      <p className="text-sm text-surface-600">Loading...</p>
    </div>
  </div>
);

/**
 * In-layout loading spinner (for route transitions within MainLayout)
 */
const InlineLoader = () => (
  <div className="flex items-center justify-center py-24 animate-fade-in">
    <div className="flex flex-col items-center gap-3">
      <Loader2 size={20} className="text-primary-500 animate-spin" />
      <p className="text-sm text-surface-500">Loading page...</p>
    </div>
  </div>
);

/**
 * AppRouter — Top-level route configuration
 *
 * Route hierarchy:
 *
 *   /login, /register, ...       → AuthLayout   → auth pages (public)
 *   /oauth/callback, /invite/... → No layout    → standalone pages (public)
 *   /, /projects, /tasks, ...    → MainLayout   → app pages (protected)
 *     └─ some with RoleRoute                     (role-gated)
 *   /*                           → NotFound     → 404 page
 */
const AppRouter = () => {
  return (
    <Routes>
      {/* ═══ AUTH ROUTES — rendered inside AuthLayout ═══ */}
      <Route
        element={
          <Suspense fallback={<PageLoader />}>
            <AuthLayout />
          </Suspense>
        }
      >
        {authRoutes.map(({ path, element: Element }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<PageLoader />}>
                <Element />
              </Suspense>
            }
          />
        ))}
      </Route>

      {/* ═══ STANDALONE PUBLIC ROUTES — no layout wrapper ═══ */}
      {standalonePublicRoutes.map(({ path, element: Element }) => (
        <Route
          key={path}
          path={path}
          element={
            <Suspense fallback={<PageLoader />}>
              <Element />
            </Suspense>
          }
        />
      ))}

      {/* ═══ CLIENT PORTAL — separate layout, separate auth ═══ */}
      <Route
        path="/client-portal/login"
        element={
          <Suspense fallback={<PageLoader />}>
            <ClientLogin />
          </Suspense>
        }
      />
      <Route
        path="/client-portal/accept-invite"
        element={
          <Suspense fallback={<PageLoader />}>
            <ClientLogin />
          </Suspense>
        }
      />
      <Route
        path="/client-portal"
        element={
          <Suspense fallback={<PageLoader />}>
            <ClientLayout />
          </Suspense>
        }
      >
        <Route index element={<Suspense fallback={<InlineLoader />}><ClientPortalPage /></Suspense>} />
        <Route path="roadmap" element={<Suspense fallback={<InlineLoader />}><ClientRoadmap /></Suspense>} />
        <Route path="tasks" element={<Suspense fallback={<InlineLoader />}><ClientTasks /></Suspense>} />
        <Route path="requests" element={<Suspense fallback={<InlineLoader />}><ClientRequests /></Suspense>} />
        <Route path="approvals" element={<Suspense fallback={<InlineLoader />}><ClientApprovals /></Suspense>} />
        <Route path="documents" element={<Suspense fallback={<InlineLoader />}><ClientDocuments /></Suspense>} />
        <Route path="budget" element={<Suspense fallback={<InlineLoader />}><ClientBudget /></Suspense>} />
        <Route path="reports" element={<Suspense fallback={<InlineLoader />}><ClientReportPage /></Suspense>} />
        <Route path="messages" element={<Suspense fallback={<InlineLoader />}><ClientMessages /></Suspense>} />
        <Route path="calendar" element={<Suspense fallback={<InlineLoader />}><ClientCalendarPage /></Suspense>} />
        <Route path="scheduling" element={<Suspense fallback={<InlineLoader />}><ClientSchedulingPage /></Suspense>} />
        <Route path="knowledge-base" element={<Suspense fallback={<InlineLoader />}><ClientKBPage /></Suspense>} />
        <Route path="time-logs" element={<Suspense fallback={<InlineLoader />}><ClientTimeLogsPage /></Suspense>} />
        <Route path="milestones" element={<Suspense fallback={<InlineLoader />}><ClientMilestonesPage /></Suspense>} />
        <Route path="activity" element={<Suspense fallback={<InlineLoader />}><ClientActivityPage /></Suspense>} />
        <Route path="ai-chat" element={<Suspense fallback={<InlineLoader />}><ClientAIChatPage /></Suspense>} />
        <Route path="delays" element={<Suspense fallback={<InlineLoader />}><ClientDelaysPage /></Suspense>} />
        <Route path="digest" element={<Suspense fallback={<InlineLoader />}><ClientDigestPage /></Suspense>} />
        <Route path="trends" element={<Suspense fallback={<InlineLoader />}><ClientTrendsPage /></Suspense>} />
        <Route path="sprints" element={<Suspense fallback={<InlineLoader />}><ClientSprintsPage /></Suspense>} />
        <Route path="goals" element={<Suspense fallback={<InlineLoader />}><ClientGoalsPage /></Suspense>} />
        <Route path="notifications" element={<Suspense fallback={<InlineLoader />}><ClientNotificationsPage /></Suspense>} />
        <Route path="forum" element={<Suspense fallback={<InlineLoader />}><ClientForumPage /></Suspense>} />
        <Route path="surveys" element={<Suspense fallback={<InlineLoader />}><ClientSurveysPage /></Suspense>} />
        <Route path="uploads" element={<Suspense fallback={<InlineLoader />}><ClientUploadsPage /></Suspense>} />
        <Route path="changelog" element={<Suspense fallback={<InlineLoader />}><ClientChangelogPage /></Suspense>} />
        <Route path="preferences" element={<Suspense fallback={<InlineLoader />}><ClientPreferencesPage /></Suspense>} />
        <Route path="finance" element={<Suspense fallback={<InlineLoader />}><ClientFinancePage /></Suspense>} />
        <Route path="portfolio" element={<Suspense fallback={<InlineLoader />}><ClientMultiDashboardPage /></Suspense>} />
        <Route path="team-contacts" element={<Suspense fallback={<InlineLoader />}><ClientTeamContactsPage /></Suspense>} />
        <Route path="meeting-history" element={<Suspense fallback={<InlineLoader />}><ClientMeetingHistoryPage /></Suspense>} />
        <Route path="profile" element={<Suspense fallback={<InlineLoader />}><ClientProfilePage /></Suspense>} />
      </Route>

      {/* ═══ STANDALONE APP ROUTES — ProtectedRoute, NO MainLayout ═══
           Authenticated exactly like the rest of the app, but rendered
           full-window with their own chrome. HiveLink brings its own rail and
           sidebar, so wrapping it in MainLayout would show two sidebars. ═══ */}
      {standaloneAppRoutes.map(({ path, element: Element }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              {/* CopilotProvider lives inside MainLayout, which these routes
                  deliberately skip — so Zuno had no page context here and its
                  '/hivelink' mapping was unreachable. HiveLink is still part of
                  Zentrix; it just renders in its own tab. The provider only
                  needs the router (useLocation/useNavigate), so mounting it
                  here restores the context without pulling in the layout. */}
              <CopilotProvider>
                <Suspense fallback={<PageLoader />}><Element /></Suspense>
              </CopilotProvider>
            </ProtectedRoute>
          }
        />
      ))}

      {/* ═══ APP ROUTES — ProtectedRoute → MainLayout ═══ */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {appRoutes.map(({ path, element: Element, minRole, permission, anyOf, allOf }) => {
          // P10 refactor — routes can gate by permission (preferred) or by
          // legacy minRole (kept working during incremental migration).
          // PermissionRoute handles both. Routes with neither simply render.
          const gated = permission || anyOf || allOf || minRole;
          return (
            <Route
              key={path}
              path={path}
              element={
                <Suspense fallback={<InlineLoader />}>
                  {gated ? (
                    <PermissionRoute permission={permission} anyOf={anyOf} allOf={allOf} minRole={minRole}>
                      <Element />
                    </PermissionRoute>
                  ) : (
                    <Element />
                  )}
                </Suspense>
              }
            />
          );
        })}
      </Route>

      {/* ═══ SUPER ADMIN — separate portal ═══ */}
      <Route
        path="/sa/login"
        element={<Suspense fallback={<PageLoader />}><SALogin /></Suspense>}
      />
      <Route
        path="/sa/*"
        element={<Suspense fallback={<PageLoader />}><SAPortal /></Suspense>}
      />
      <Route
        path="/sa"
        element={<Suspense fallback={<PageLoader />}><SAPortal /></Suspense>}
      />

      {/* ═══ 404 CATCH ALL ═══ */}
      <Route
        path="*"
        element={
          <Suspense fallback={<PageLoader />}>
            <NotFound />
          </Suspense>
        }
      />
    </Routes>
  );
};

export default AppRouter;